/**
 * Renders a Contentful `productCategoryPage` entry: a metal category landing page at `/<slug>`
 * (for example `/aluminum`). `pages/[...path].tsx` builds one page per entry at build time and
 * renders this component when no Contentful `page` claims the same path. The entries are not
 * filtered by brand, so the dormant Southern Tool Steel build exports them too.
 *
 * Page order: `MetaHead`; the hero (`components/Products/Hero.tsx`); the `SubNavigation` link
 * bar, from the `xl` breakpoint up (`greaterThan="lg"`); then five `AllSections` sections:
 * Applications, Characteristics & Properties, By The Numbers, What Our Customers Say, and Metal
 * Capabilities (Ryerson) or Our Solutions (Southern Tool Steel). Headings and labels come from
 * `content/ryerson/products/productStaticContent.ts` and
 * `components/Products/MetalCapabilities/Content.tsx`. The What Our Customers Say heading and
 * quotes come from `components/Shared/WhatOurCustomers/RefactorContent.tsx` (the carousel picks
 * its own copy). The rest comes from the entry.
 * The sub-category pages under it are `./SubProduct.tsx`.
 *
 * Links: the hero's "Help me find the right metal" button goes to `/store`, and each product in
 * the hero links to its `linkToProduct`; both are plain anchors (full page loads).
 *
 * SEO: the canonical URL is the page's own, `NEXT_PUBLIC_ENV_URL` + `/<slug>` (`MetaHead`'s
 * default). The category and sub-category URLs are kept out of the marketing sitemap
 * (`marketing-custom-paths.js`), and the sub-category pages point their canonical at the store
 * (`./SubProduct.tsx`). These pages are handled specially for SEO per business requirements.
 * `marketing-custom-paths.js` lists the category slugs by hand, so a new category is in the
 * sitemap until its slug is added there, both bare (`/<slug>`) and with `/**`.
 *
 * Rules a change must keep:
 * - Every field the page reads must be in {@link ProductFields}, by its full dotted path:
 *   `[...path].tsx` → `getStaticProps` passes the list to `Crawl` as `include`, and `Crawl` drops
 *   any field not listed. Listing a parent alone keeps none of its children.
 * - Each `SubNavigation` id must equal the anchor `components/Shared/AllSections/AllSections.tsx`
 *   gives its section (the title lower-cased, whitespace runs replaced by `-`). Both are built
 *   here from the same copy, so change them together.
 * - The entry needs `products`, `generalReferences`, `applications`, `metalCapabilities` and at
 *   least one `characteristicsProperties` item; the section components read them without a guard
 *   and the page fails in the browser without them. `byTheNumbers` and `blurb2` are optional.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { MetaContent } from "@components/Shared/model/MetaHead";
import MetaHead from "@components/Shared/model/MetaHead";
import {
	RyersonMetalCapabilitiesContent,
	StsMetalCapabilitiesContent,
} from "@components/Products/MetalCapabilities/Content";
import {
	StaticHeroContent,
	CharacteristicsStaticContent,
	ByTheNumbersStaticContent,
	ApplicationsStaticContent,
} from "@content/ryerson/products/productStaticContent";
import SubNavigation, {
	NavigableItem,
} from "@components/BarTubeStructuralProcessing/SubNavigation/SubNavigation";
import HeroComponent from "@components/Products/Hero";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import ApplicationsComponent from "@components/Products/Applications";
import CharacteristicsComponent from "@components/Products/Characteristics";
import ByTheNumbersComponent from "@components/Products/ByTheNumbers";
import { WhatOurContentSays } from "@components/Shared/WhatOurCustomers/RefactorContent";
import WhatOurCustomers from "@components/Shared/WhatOurCustomers/WhatOurCustomersRefactor";
import MetalCapabilitiesComponent from "@components/Products/MetalCapabilities/MetalCapabilities";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import { companyToCompany } from "@components/Shared/model/Contentful";
import { IContentfulBaseFields } from "./ContentfulPage";

/**
 * One language of a `productCategoryPage` entry, as `Crawl` returns it with
 * {@link ProductFields}. The nested shapes match `components/Products/Types/Types.tsx`.
 */
export interface ContentfulProductCategoryPageContent extends IContentfulBaseFields {
	applications: string[];
	applicationsBlurb: string;
	// Hero intro text.
	blurb: string;
	// Optional second blurb in the hero: its last block on desktop, between the product list and
	// the General Reference PDFs on mobile.
	blurb2: string;
	byTheNumbers: {
		blurb: string;
		citation: string;
		heading: string;
	}[];
	characteristicsProperties: {
		description: string;
		title: string;
	}[];
	characteristicsPropertiesBlurb: string;
	generalReferences: {
		pdf: {
			file: {
				url: string;
			};
		};
		title: string;
	}[];
	metalCapabilities: {
		// The brands that show this capability (`MetalCapabilities.tsx` filters on it).
		company: ("Ryerson" | "Southern Tool Steel")[];
		description: string;
		image: {
			file: {
				url: string;
			};
		};
		link: string;
		title: string;
	}[];
	// The hero's product list.
	products: {
		linkToProduct: string;
		productName: string;
		productRendering: {
			file: {
				url: string;
			};
		};
	}[];
	seoDescription: string;
	// `og:image`; its URL has no scheme, so the page prefixes `https:`.
	seoImage: {
		file: {
			url: string;
		};
	};
	seoTitle: string;
	// The page's URL path segment: the page is at `/<slug>`.
	slug: string;
	title: string;
	// Not in `ProductFields`, so `Crawl` drops it and it is always absent here. Nothing reads it.
	shoppingLink: string;
}

/**
 * The field paths `pages/[...path].tsx` → `getStaticProps` keeps from a `productCategoryPage`
 * response (`Crawl` `include`). A field the page reads must be listed by its full dotted path.
 */
export const ProductFields = [
	"applications",
	"applicationsBlurb",
	"blurb",
	"blurb2",
	"byTheNumbers",
	"byTheNumbers.blurb",
	"byTheNumbers.citation",
	"byTheNumbers.heading",
	"characteristicsProperties",
	"characteristicsProperties.description",
	"characteristicsProperties.title",
	"characteristicsPropertiesBlurb",
	"generalReferences",
	"generalReferences.pdf",
	"generalReferences.pdf.file",
	"generalReferences.pdf.file.url",
	"generalReferences.title",
	"metalCapabilities",
	"metalCapabilities.company",
	"metalCapabilities.description",
	"metalCapabilities.image",
	"metalCapabilities.image.file",
	"metalCapabilities.image.file.url",
	"metalCapabilities.link",
	"metalCapabilities.title",
	"products",
	"products.linkToProduct",
	"products.productName",
	"products.productRendering",
	"products.productRendering.file",
	"products.productRendering.file.url",
	"seoDescription",
	"seoImage",
	"seoImage.file",
	"seoImage.file.url",
	"seoTitle",
	"slug",
	"title",
];

/** Props of {@link ProductPage}. */
export type ProductPageProps = {
	/** The entry in every language (`en`, `fr`, `es`), from `[...path].tsx`. */
	contentfulProductPage: LanguageContent<ContentfulProductCategoryPageContent>;
};

/** The product-category page body, in the visitor's language; see the file header. */
const ProductPage: React.FC<ProductPageProps> = ({ contentfulProductPage }) => {
	const {
		localization: { language },
	} = useApplication();

	const content = contentfulProductPage[language];

	const company = companyToCompany(process.env.NEXT_PUBLIC_COMPANY || "");

	let MetaObject: LanguageContent<MetaContent> = {
		en: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
		},
		fr: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
		},
		es: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
		},
	};

	const subNav: NavigableItem[] = [];
	const sections: Sections[] = [];

	subNav.push({
		id: ApplicationsStaticContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ApplicationsStaticContent[language].navLabel,
	});
	sections.push({
		title: ApplicationsStaticContent[language].title,
		cmp: (
			<ApplicationsComponent
				staticContent={ApplicationsStaticContent[language]}
				dynamicContent={{
					description: content.applicationsBlurb,
					applications: content.applications,
				}}
			/>
		),
	});

	subNav.push({
		id: (
			CharacteristicsStaticContent[language].title1 +
			" " +
			CharacteristicsStaticContent[language].title2
		)
			.toLowerCase()
			.replace(/\s+/g, "-"),
		title: CharacteristicsStaticContent[language].navLabel ?? "",
	});
	sections.push({
		title:
			CharacteristicsStaticContent[language].title1 +
			" " +
			CharacteristicsStaticContent[language].title2,
		cmp: (
			<CharacteristicsComponent
				staticContent={CharacteristicsStaticContent[language]}
				dynamicContent={{
					characteristics: content.characteristicsProperties,
					blurb: content.characteristicsPropertiesBlurb,
				}}
			/>
		),
		maxHeight: "10000px",
	});

	subNav.push({
		id: ByTheNumbersStaticContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ByTheNumbersStaticContent[language].navLabel,
	});
	sections.push({
		title: ByTheNumbersStaticContent[language].title,
		cmp: (
			<ByTheNumbersComponent
				staticContent={ByTheNumbersStaticContent[language]}
				dynamicContent={{
					byTheNumbers: content.byTheNumbers,
				}}
			/>
		),
	});

	subNav.push({
		id: WhatOurContentSays[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: WhatOurContentSays[language].title,
	});
	sections.push({
		title: WhatOurContentSays[language].title,
		cmp: <WhatOurCustomers />,
	});

	// The last section's copy depends on the brand: Ryerson's "Metal Capabilities" or Southern Tool
	// Steel's "Our Solutions" (`components/Products/MetalCapabilities/Content.tsx`).
	subNav.push({
		id: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).title
			.toLowerCase()
			.replace(/\s+/g, "-"),
		title: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).navLabel,
	});
	sections.push({
		title: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).title,
		cmp: (
			<MetalCapabilitiesComponent
				staticContent={
					company === "Southern Tool Steel"
						? StsMetalCapabilitiesContent[language]
						: RyersonMetalCapabilitiesContent[language]
				}
				dynamicContent={{
					capabilities: content.metalCapabilities,
				}}
			/>
		),
	});

	return (
		<>
			<MetaHead content={MetaObject[language]} />
			<HeroComponent
				staticContent={StaticHeroContent[language]}
				dynamicContent={{
					title: content.title,
					blurb: content.blurb,
					blurb2: content.blurb2,
					products: content.products,
					references: content.generalReferences,
				}}
			/>
			{/* `greaterThan="lg"` is `xl` and up; `SubProduct.tsx` shows its bar from `lg`. */}
			<Media greaterThan="lg">
				<SubNavigation navigableItems={subNav} type="primary" />
			</Media>
			<AllSections sections={sections} />
		</>
	);
};

export default ProductPage;
